import { ActionError, Button, WaitPhrase, cn } from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import { isEmail, useRequestCode } from './api'
import { AuthLayout, FieldNote, authInput, authLink } from './auth-layout'
import { RESEND_AFTER_SECONDS, useSecondsUntil } from './resend-timer'

/**
 * Entrar com código (docs/areas/autenticacao.md, "Entrar com código"; RN-AUT-07, T48 e
 * docs/api/acesso.md): o login rápido, sem senha. Pede o e-mail (`POST /access/code`, resposta
 * sempre igual), depois o código de 6 dígitos (conferido pelo Supabase Auth) e, com a sessão
 * aberta, segue para a porta de entrada, como o login por senha. Nada de senha depois do código:
 * quem quiser criar ou trocar a senha faz em Sua conta › Senha. Segue o leiaute do login (P-AUT-12).
 */

const MESSAGES = {
  email: 'Confira o e-mail: precisa ter @ e um domínio, como nome@clinica.com.br.',
  codeLength: 'O código tem 6 dígitos. Confira o e-mail e digite todos.',
  invalid_code: 'O código não confere ou venceu. Peça outro.',
  rate_limited: 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.',
  refused: 'Não foi possível entrar agora. Tente de novo em instantes.',
} as const

export function SignInCodePage() {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const request = useRequestCode()
  const emailId = useId()
  const codeId = useId()
  // Quem já digitou o e-mail no login não digita de novo.
  const [email, setEmail] = useState(() => {
    const typed = (location.state as { email?: unknown } | null)?.email
    return typeof typed === 'string' ? typed : ''
  })
  const [emailTouched, setEmailTouched] = useState(false)
  // A recusa da API vale até a pessoa mexer no campo.
  const [emailRefused, setEmailRefused] = useState<string | null>(null)
  // O pedido aceito: para qual e-mail, a frase da API e quando (libera o reenvio).
  const [sent, setSent] = useState<{ email: string; message: string; at: number } | null>(null)
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [codeRefused, setCodeRefused] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)
  const secondsToResend = useSecondsUntil(sent ? sent.at + RESEND_AFTER_SECONDS * 1000 : null)

  // Quem já entrou não tem o que fazer aqui. Durante a conferência quem decide o destino é a tela.
  if (!verifying && session.status === 'signed_in') return <Navigate to="/" replace />
  // Sessão aberta com a senha temporária, pela senha: a única tela possível é a da troca (RT-04).
  if (!verifying && session.status === 'password_required') {
    return <Navigate to={routes.newPassword} replace />
  }

  const emailError = emailRefused ?? (emailTouched && !isEmail(email) ? MESSAGES.email : null)
  const codeError = codeRefused ?? (codeTouched && code.length !== 6 ? MESSAGES.codeLength : null)
  // Erro que não é do campo (limite de tentativas, falha de rede) aparece inteiro.
  const requestError =
    request.isError && !fieldErrors(request.error).email ? errorMessage(request.error) : null

  async function requestCode(address: string) {
    if (request.isPending) return
    setRefusal(null)
    try {
      const result = await request.mutateAsync(address)
      setSent({ email: address, message: result.message, at: Date.now() })
      setCode('')
      setCodeTouched(false)
      setCodeRefused(null)
    } catch (error) {
      setEmailRefused(fieldErrors(error).email ?? null)
    }
  }

  function submitEmail(event: FormEvent) {
    event.preventDefault()
    setEmailTouched(true)
    if (!isEmail(email)) return
    void requestCode(email.trim().toLowerCase())
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    setCodeTouched(true)
    if (!sent || code.length !== 6 || verifying) return
    setVerifying(true)
    setCodeRefused(null)
    setRefusal(null)
    const result = await session.verifyCode(sent.email, code)
    if (result.error) {
      setVerifying(false)
      if (result.error.kind === 'invalid_code') setCodeRefused(MESSAGES.invalid_code)
      else setRefusal(result.error.message ?? MESSAGES[result.error.kind])
      return
    }
    // Com a sessão aberta, a porta de entrada decide o destino (escolha de clínica, implantação ou
    // painel), como no login por senha. O código não pede nem oferece senha (T48).
    navigate('/', { replace: true })
  }

  const backToLogin = (
    <Link to={routes.login} className={authLink}>
      Voltar para entrar com senha
    </Link>
  )

  if (!sent) {
    return (
      <AuthLayout title="Entrar com código" onSubmit={submitEmail}>
        <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
          Entre sem senha: informe o e-mail da sua conta e enviamos um código de 6 dígitos para você
          entrar.
        </p>

        <label htmlFor={emailId} className="eyebrow mt-8">
          E-mail
        </label>
        <input
          id={emailId}
          type="email"
          autoComplete="username"
          inputMode="email"
          value={email}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? `${emailId}-error` : undefined}
          onChange={(event) => {
            setEmail(event.target.value)
            setEmailRefused(null)
          }}
          onBlur={() => setEmailTouched(true)}
          className={authInput}
        />
        <FieldNote id={`${emailId}-error`} error={emailError} />

        {requestError ? (
          <ActionError
            className="mt-6"
            title="Não foi possível enviar o código"
            description={requestError}
          />
        ) : null}

        <Button
          type="submit"
          size="lg"
          className="mt-7"
          loading={request.isPending}
          loadingLabel="Enviando…"
        >
          Enviar código
        </Button>
        <div className="mt-5 flex">{backToLogin}</div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Entrar com código" onSubmit={submitCode}>
      <p className="font-body text-secondary mt-3 text-[13px] leading-[1.6] font-light">
        {sent.message}
      </p>
      <p className="font-body text-label mt-2 text-[12px] leading-[1.6] font-light break-words">
        E-mail informado: <span className="text-ink">{sent.email}</span>
      </p>

      <label htmlFor={codeId} className="eyebrow mt-8">
        Código
      </label>
      <input
        id={codeId}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        value={code}
        aria-invalid={codeError ? true : undefined}
        aria-describedby={codeError ? `${codeId}-error` : undefined}
        onChange={(event) => {
          // Sem `maxLength`: o e-mail mostra "482 913", e o navegador cortaria o último dígito ao
          // colar. O corte vem depois de tirar o que não é dígito.
          setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
          setCodeRefused(null)
        }}
        onBlur={() => setCodeTouched(true)}
        className={cn(authInput, 'font-display text-[18px] tracking-[.3em]')}
      />
      <FieldNote id={`${codeId}-error`} error={codeError} />

      {refusal ? (
        <ActionError className="mt-6" title="Não foi possível entrar" description={refusal} />
      ) : null}
      {requestError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível enviar outro código"
          description={requestError}
        />
      ) : null}

      <Button type="submit" size="lg" className="mt-7" loading={verifying} loadingLabel="Entrando…">
        Entrar
      </Button>
      <Button
        variant="outline"
        size="lg"
        className="mt-3"
        disabled={secondsToResend > 0}
        loading={request.isPending}
        loadingLabel="Enviando…"
        onClick={() => void requestCode(sent.email)}
      >
        {secondsToResend > 0
          ? `Enviar outro código em ${secondsToResend} s`
          : 'Enviar outro código'}
      </Button>
      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-7">
        <button
          type="button"
          onClick={() => {
            setSent(null)
            setRefusal(null)
            request.reset()
          }}
          className={authLink}
        >
          Usar outro e-mail
        </button>
        {backToLogin}
      </div>
      <WaitPhrase moment="signing_in" active={verifying} className="mt-4" />
    </AuthLayout>
  )
}
